/// <amd-module name="Repos/Tfvc/Files/TfvcFilesService" />
import { ChangesFilesService } from "Repos/ChangesCommon/ChangesFilesService";
import { IChangeItemContent, IChangesFilesService } from "Repos/ChangesCommon/Contracts";
import { FileContentMetadata, ItemModel, TfvcItem, TfvcVersionDescriptor, VersionControlRecursionType } from "Repos/Common/Contracts/Git";
import { getTfvcResourceUri } from "Repos/Common/Util/Editor";
import { getTfvcVersionString, tfvcVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssRestClient, IVssService, RestClients, Services } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";
import { combineUrlPaths, Uri } from "VSS/Platform/Util/Url";

export interface ITfvcItemContent extends IChangeItemContent {}

export interface ITfvcItemModel extends ItemModel {
    version: string;
}

export interface ITfvcFilesService extends IVssService, IChangesFilesService<TfvcVersionDescriptor> {
    getItemContentPromise(path: string, version: TfvcVersionDescriptor): Promise<ITfvcItemContent>;
}

class TfvcFilesService extends ChangesFilesService<TfvcVersionDescriptor> implements ITfvcFilesService {
    private projectId: string;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        this.projectId = vcViewModelDataProviderService.getViewModel().projectVersionControlInfo.project.id;
    }

    public convertDescriptorToVersion(versionDescriptor: TfvcVersionDescriptor): string {
        return getTfvcVersionString(versionDescriptor);
    }

    public convertVersionToDescriptor(version: string): TfvcVersionDescriptor {
        return tfvcVersionStringToVersionDescriptor(version);
    }

    public getFileDownloadUrl(path: string, version: TfvcVersionDescriptor, forceDownload: boolean = false): string {
        const hostPath = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostPath;
        const url = new Uri(combineUrlPaths(hostPath, `${this.projectId}/_apis/tfvc/items`));

        url.addQueryParams({
            path: path,
            ["versionDescriptor[versionType]"]: version.versionType || 0,
            ["versionDescriptor[version]"]: version.version,
            ["versionDescriptor[versionOption]"]: version.versionOption,
            $format: "octetStream",
            ["api-version"]: "5.0"
        });

        if (forceDownload) {
            url.addQueryParam("download", "true");
        }

        return url.absoluteUri;
    }

    public getItemContent(path: string, version: TfvcVersionDescriptor): IReadonlyObservableValue<ITfvcItemContent | undefined> {
        const contentObservable = new ObservableValue<ITfvcItemContent | undefined>(undefined);

        this.getItemContentPromise(path, version).then(content => {
            contentObservable.value = content;
        });

        return contentObservable;
    }

    public async getItemContentPromise(path: string, version: TfvcVersionDescriptor): Promise<ITfvcItemContent> {
        const client = this.pageContext.getRestClient<ITfvcItemContentRestClient>(TfvcItemContentRestClientName);
        try {
            const result = await client.getItemContent(path, this.projectId, version);
            const item = result.item;
            return {
                commitId: getTfvcVersionString(version),
                details: {
                    content: item.content,
                    contentTruncated: result.truncated,
                    metadata: item.contentMetadata,
                    objectId: item.hashValue,
                    resourceUri: getTfvcResourceUri(version, path)
                }
            };
        } catch (error) {
            return {
                commitId: "",
                details: {
                    content: "",
                    contentTruncated: false,
                    metadata: {} as FileContentMetadata,
                    objectId: "",
                    resourceUri: getTfvcResourceUri(version, path)
                },
                error
            };
        }
    }

    public getObjectId(itemModel: ITfvcItemModel): string {
        return itemModel.version;
    }
}

const serviceName = "ITfvcFilesService";

Services.add(serviceName, { serviceFactory: TfvcFilesService });

export function getTfvcFilesService(pageContext: IVssPageContext): ITfvcFilesService {
    return pageContext.getService<ITfvcFilesService>(serviceName);
}

/**
 * REST client to get item content honoring a 206 (Partial Content) response to indicate that the content was truncated
 */
interface ITfvcItemContentRestClient extends IVssRestClient {
    getItemContent(path: string, project: string, versionDescriptor: TfvcVersionDescriptor): Promise<{ item: TfvcItem; truncated: boolean }>;
}

class TfvcItemContentRestClient extends RestClientBase implements ITfvcItemContentRestClient {
    public async getItemContent(
        path: string,
        project: string,
        versionDescriptor: TfvcVersionDescriptor
    ): Promise<{ item: TfvcItem; truncated: boolean }> {
        const response = await this.beginRequest<Response>({
            apiVersion: "5.0",
            queryParams: {
                path: path,
                recursionLevel: VersionControlRecursionType.None,
                versionDescriptor: versionDescriptor,
                includeContent: true
            },
            routeTemplate: "{project}/_apis/tfvc/Items/{*path}",
            routeValues: {
                project: project
            },
            returnRawResponse: true
        });

        return {
            item: (await response.text().then(deserializeVssJsonObject)) as TfvcItem,
            truncated: response.status === 206
        };
    }
}

const TfvcItemContentRestClientName = "Repos.Files.Tfvc.IGitItemContentRestClient";

RestClients.add(TfvcItemContentRestClientName, {
    factory: TfvcItemContentRestClient,
    options: {
        resourceAreaId: "8aa40520-446d-40e6-89f6-9c9f9ce44c48",
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
